import { useState, useEffect, useCallback } from 'react'
import Core from '@hcm4all/frontend-core'

import EventFormCalendar from './EventFormCalendar'
import VacationEventsStats from '../calendar/VacationEventsStats'
import FileUpload from './FileUpload'
import EventTypeSelect from './EventTypeSelect'
import ExtraDaysWarning from './ExtraDaysWarning'
import SplitEventView from './SplitEventView'

// const { Calendar } = Core.UI.SVG.Icons16
const { ArrowLeft, Calendar20 } = Core.UI.SVG.Icons

export default function EventForm (props) {
  const {
    event,
    employee,
    eventTypes,
    stats,
    statsNextYear,
    formUrl,
    backUrl,
    splitsUrl,
    csrfToken,
    title,
    eauWarning,
    eauSettingsUrl,
    isPortal,
    contractUrl,
    notifyHrUrl,
    userIsAdmin
  } = props

  const [startDate, setStartDate] = useState(event.start_date || '')
  const [endDate, setEndDate] = useState(event.end_date || '')
  const [halfDay, setHalfDay] = useState(event.half_day === true || event.half_day === '1' || event.half_day === 'true')
  const [eventTypeId, setEventTypeId] = useState(event.event_type_id || '')
  const [description, setDescription] = useState(event.description || '')
  const [file, setFile] = useState(null)
  const [destroyAttachment, setDestroyAttachment] = useState(false)
  const [vacationInAdvance, setVacationInAdvance] = useState(false)
  const [holidayInAdvanceEnabled, setHolidayInAdvanceEnabled] = useState(false)

  const [requestedDays, setRequestedDays] = useState(0)
  const [workingDays, setWorkingDays] = useState(0)
  const [needExtraDays, setNeedExtraDays] = useState(false)
  const [allowInAdvance, setAllowInAdvance] = useState(false)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [overlapError, setOverlapError] = useState(null)
  const [noWorkingDaysWarning, setNoWorkingDaysWarning] = useState(false)

  // Split event state
  const [showSplitView, setShowSplitView] = useState(false)
  const [splitPreview, setSplitPreview] = useState([])
  const [isSplitSubmitting, setIsSplitSubmitting] = useState(false)

  const [calendarSettings, setCalendarSettings] = useState({
    enabledDates: [],
    disabledDates: [],
    maxAllowedInAdvanceDays: 0,
    allowInAdvanceVacations: false
  })

  const isMainVacation = event.main_vacation
  const isPersisted = !!event.id
  const isPending = event.status === 'pending'
  const hasGeoAndWorkingTimeModel = employee.geo && employee.working_time_model

  // Check if selected event type requires attachment
  const selectedEventType = eventTypes.find(et => String(et.id) === String(eventTypeId))
  const isAttachmentRequired = !isMainVacation && (selectedEventType?.attachment_required || false)
  const hasAttachment = file || (event.attachment_relation && !destroyAttachment)

  useEffect(() => {
    fetchCalendarSettings()
  }, [])

  useEffect(() => {
    if (startDate && endDate && isMainVacation) {
      fetchHolidayRequestSummary()
    }
  }, [startDate, endDate, halfDay, isMainVacation])

  const fetchCalendarSettings = async () => {
    try {
      const response = await fetch('/calendar/settings.json')
      const data = await response.json()
      if (data) {
        setCalendarSettings({
          enabledDates: data.enabled_dates || [],
          disabledDates: event.events_dates || [],
          maxAllowedInAdvanceDays: data.max_allowed_in_advance_days || 0,
          allowInAdvanceVacations: data.allow_in_advance_vacations || false
        })
      }
    } catch (error) {
      console.error('Failed to fetch calendar settings:', error)
    }
  }

  const fetchHolidayRequestSummary = async () => {
    if (!startDate || !endDate) return

    setIsLoading(true)
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      employee_id: employee.id,
      half_day: halfDay
    })

    try {
      const response = await fetch(`/calendar/holiday_request_summary.json?${params.toString()}`)
      const data = await response.json()

      let calculatedWorkingDays = data.working_days
      if (halfDay && data.working_days === 1) {
        calculatedWorkingDays = 0.5
      }

      setRequestedDays(data.requested_days)
      setWorkingDays(calculatedWorkingDays)
      setNeedExtraDays(data.need_extra_days)
      setAllowInAdvance(data.allow_in_advance)

      // Check if selected days contain no working days
      const hasNoWorkingDays = data.requested_days > 0 && calculatedWorkingDays === 0
      setNoWorkingDaysWarning(hasNoWorkingDays)

      if (hasNoWorkingDays) {
        setIsSubmitDisabled(true)
      } else if (data.need_extra_days && !data.allow_in_advance) {
        setIsSubmitDisabled(true)
      } else if (data.need_extra_days && data.allow_in_advance) {
        setIsSubmitDisabled(!holidayInAdvanceEnabled)
      } else {
        setIsSubmitDisabled(false)
      }
    } catch (error) {
      console.error('Failed to fetch holiday request summary:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDateChange = useCallback((dates) => {
    setStartDate(dates.startDate)
    setEndDate(dates.endDate)
  }, [])

  const handleHalfDayChange = (e) => {
    setHalfDay(e.target.checked)
  }

  const handleHolidayInAdvanceChange = (e) => {
    const checked = e.target.checked
    setHolidayInAdvanceEnabled(checked)
    setVacationInAdvance(checked)
    setIsSubmitDisabled(!checked)
  }

  const handleFileChange = (newFile) => {
    setFile(newFile)
  }

  const handleRemoveAttachment = () => {
    if (window.confirm(I18n.t('really_delete'))) {
      setDestroyAttachment(true)
    }
  }

  // Check if selected dates overlap with existing events (the current event
  // is excluded from disabledDates on the backend when editing)
  // Returns array of ALL overlapping events (backend processes all overlaps)
  const checkOverlaps = useCallback((start, end) => {
    if (!start || !end) return []

    const disabledDates = calendarSettings.disabledDates || []
    const overlaps = []

    for (const range of disabledDates) {
      const rangeFrom = range.from
      const rangeTo = range.to
      // Check if date ranges overlap
      if (start <= rangeTo && end >= rangeFrom) {
        overlaps.push({ from: rangeFrom, to: rangeTo, title: range.title })
      }
    }
    return overlaps
  }, [calendarSettings.disabledDates])

  // Check for overlap when dates change
  useEffect(() => {
    const overlaps = checkOverlaps(startDate, endDate)
    // For UI we use first overlap, but split preview uses all
    setOverlapError(overlaps.length > 0 ? overlaps : null)
  }, [startDate, endDate, checkOverlaps])

  // Calculate split preview based on overlaps
  // When creating an OTHER ABSENCE that overlaps with existing HOLIDAYS,
  // the existing holidays will be split/cut around the new other absence
  // Processes ALL overlapping events like backend does
  const calculateSplitPreview = useCallback(() => {
    // Split only works when creating other absence (not main vacation)
    if (!overlapError || !Array.isArray(overlapError) || overlapError.length === 0 || isMainVacation) return []

    const newStart = new Date(startDate)
    const newEnd = new Date(endDate)
    const preview = []

    // Process each overlapping event
    for (const overlap of overlapError) {
      const overlapStart = new Date(overlap.from)
      const overlapEnd = new Date(overlap.to)

      // Part of existing holiday BEFORE the new other absence
      if (overlapStart < newStart) {
        const beforeEnd = new Date(newStart)
        beforeEnd.setDate(beforeEnd.getDate() - 1)
        preview.push({
          start_date: overlap.from,
          end_date: beforeEnd.toISOString().split('T')[0],
          title: overlap.title || I18n.t('calendar.split_event.vacation'),
          main_vacation: true
        })
      }

      // Part of existing holiday AFTER the new other absence
      if (overlapEnd > newEnd) {
        const afterStart = new Date(newEnd)
        afterStart.setDate(afterStart.getDate() + 1)
        preview.push({
          start_date: afterStart.toISOString().split('T')[0],
          end_date: overlap.to,
          title: overlap.title || I18n.t('calendar.split_event.vacation'),
          main_vacation: true
        })
      }
    }

    // The new other absence event
    preview.push({
      start_date: startDate,
      end_date: endDate,
      title: selectedEventType?.name || I18n.t('calendar.event_types.other'),
      main_vacation: false
    })

    // Sort by start date
    preview.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))

    return preview
  }, [overlapError, startDate, endDate, isMainVacation, selectedEventType])

  // Update split preview when overlap changes
  useEffect(() => {
    if (overlapError && !isMainVacation) {
      setSplitPreview(calculateSplitPreview())
    }
  }, [overlapError, isMainVacation, calculateSplitPreview])

  const handleSubmit = async (e) => {
    e.preventDefault()

    // For other absence with overlap (against holidays), show split view
    if (overlapError && !isMainVacation) {
      setShowSplitView(true)
      return
    }

    // For main vacation with overlap, just show alert (can't split)
    if (overlapError && isMainVacation) {
      window.alert(I18n.t('calendar.split_event.selected_time_period'))
      return
    }

    await submitForm(formUrl)
  }

  const handleSplitConfirm = async () => {
    setIsSplitSubmitting(true)

    // Create and submit a real form - splits controller expects HTML form submission
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = splitsUrl

    const fields = {
      'calendar_event[event_type_id]': eventTypeId || event.event_type_id,
      'calendar_event[start_date]': startDate,
      'calendar_event[end_date]': endDate,
      'calendar_event[half_day]': halfDay ? '1' : '0',
      'calendar_event[description]': description,
      'calendar_event[main_vacation]': isMainVacation ? '1' : '0',
      authenticity_token: csrfToken
    }

    if (vacationInAdvance) {
      fields['calendar_event[vacation_in_advance]'] = 'true'
    }

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  const submitForm = async (url) => {
    // Create and submit a real form - controller expects HTML form submission
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = url
    form.enctype = 'multipart/form-data'

    // For PATCH requests (editing), add _method field
    if (isPersisted) {
      const methodInput = document.createElement('input')
      methodInput.type = 'hidden'
      methodInput.name = '_method'
      methodInput.value = 'PATCH'
      form.appendChild(methodInput)
    }

    const fields = {
      'calendar_event[event_type_id]': eventTypeId || event.event_type_id,
      'calendar_event[start_date]': startDate,
      'calendar_event[end_date]': endDate,
      'calendar_event[half_day]': halfDay ? '1' : '0',
      'calendar_event[description]': description,
      'calendar_event[main_vacation]': isMainVacation ? '1' : '0',
      authenticity_token: csrfToken
    }

    if (vacationInAdvance) {
      fields['calendar_event[vacation_in_advance]'] = 'true'
    }

    if (destroyAttachment) {
      fields['calendar_event[destroy_attachment]'] = 'true'
    }

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    })

    // Handle file upload
    if (file) {
      const fileInput = document.createElement('input')
      fileInput.type = 'file'
      fileInput.name = 'calendar_event[file]'
      fileInput.style.display = 'none'

      // Create a DataTransfer to set the file
      const dataTransfer = new window.DataTransfer()
      dataTransfer.items.add(file)
      fileInput.files = dataTransfer.files

      form.appendChild(fileInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  const handleNotifyHr = async (fieldName) => {
    try {
      const response = await fetch(notifyHrUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ field_name: fieldName }),
        redirect: 'manual'
      })

      if (response.ok || response.type === 'opaqueredirect') {
        window.location.href = response.headers.get('Location') || window.location.href
      } else {
        console.error('Failed to notify HR:', response.status)
      }
    } catch (error) {
      console.error('Failed to notify HR:', error)
    }
  }

  if (!hasGeoAndWorkingTimeModel) {
    return (
      <div className='event-form'>
        {!employee.geo && (
          <div className='event-form-overlap-warning'>
            <p className='event-form-overlap-warning__text'>
              {I18n.t('calendar.missing_geo.description')}{' '}
              {isPortal
                ? (
                  <>
                    {I18n.t('calendar.missing_geo.notify_hr_desc')}:&nbsp;
                    <a href='#' onClick={(e) => { e.preventDefault(); handleNotifyHr('geo') }}>
                      {I18n.t('calendar.missing_geo.notify_hr')}
                    </a>
                  </>
                  )
                : (
                  <>
                    {I18n.t('calendar.missing_geo.fix_geo_desc')}:&nbsp;
                    <a href={contractUrl} target='_blank' rel='noreferrer'>
                      {I18n.t('calendar.missing_geo.fix_geo')}
                    </a>
                  </>
                  )}
            </p>
          </div>
        )}
        {!employee.working_time_model && (
          <div className='event-form-overlap-warning'>
            <p className='event-form-overlap-warning__text'>
              {I18n.t('calendar.missing_working_time_model.description')}{' '}
              {isPortal
                ? (
                  <>
                    {I18n.t('calendar.missing_working_time_model.notify_hr_desc')}:&nbsp;
                    <a href='#' onClick={(e) => { e.preventDefault(); handleNotifyHr('working_time_model') }}>
                      {I18n.t('calendar.missing_working_time_model.notify_hr')}
                    </a>
                  </>
                  )
                : (
                  <>
                    {I18n.t('calendar.missing_working_time_model.fix_working_time_model_desc')}:&nbsp;
                    <a href={contractUrl} target='_blank' rel='noreferrer'>
                      {I18n.t('calendar.missing_working_time_model.fix_working_time_model')}
                    </a>
                  </>
                  )}
            </p>
          </div>
        )}
      </div>
    )
  }

  // Show split view when user submitted other absence form with overlap against holidays
  if (showSplitView && overlapError && !isMainVacation && !isPersisted) {
    return (
      <div className='event-form'>
        <div className='event-form__header'>
          <a href={backUrl} className='event-form__back-btn'>
            <ArrowLeft />
          </a>
          <h2 className='event-form__title'>{title}</h2>
        </div>

        <div className='event-form__divider' />

        <SplitEventView
          backUrl={backUrl}
          onConfirm={handleSplitConfirm}
          overlapInfo={overlapError}
          splitPreview={splitPreview}
          isSubmitting={isSplitSubmitting}
        />
      </div>
    )
  }

  return (
    <div className='event-form'>
      <div className='event-form__header'>
        <a href={backUrl} className='event-form__back-btn'>
          <ArrowLeft />
        </a>
        <h2 className='event-form__title'>{title}</h2>
      </div>

      <div className='event-form__divider' />

      {eauWarning && (
        <div className='alert alert-warning'>
          <span>
            {`${eauWarning} `}
            {userIsAdmin &&
              <a href={eauSettingsUrl}>
                {I18n.t('eau.calendar/event.client_warning_url_to_settings')}
              </a>}
          </span>
        </div>
      )}

      {isPersisted && isPending && (
        <div className='alert alert-warning'>
          {I18n.t('calendar.holiday_entitlements.pending_request_warning')}
        </div>
      )}

      {overlapError && overlapError.length > 0 && isMainVacation && (
        <div className='event-form-overlap-warning'>
          <p className='event-form-overlap-warning__text'>
            {I18n.t('activerecord.errors.models.calendar/event.attributes.base.has_overlaps', {
              periods: overlapError.map(o => `${o.from} - ${o.to}`).join(', ')
            })}
          </p>
        </div>
      )}

      {noWorkingDaysWarning && (
        <div className='event-form-overlap-warning'>
          <p className='event-form-overlap-warning__text'>
            {I18n.t('calendar.holiday_entitlements.no_working_days_warning')}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} encType='multipart/form-data'>
        {isMainVacation
          ? (
            <>
              <input type='hidden' name='calendar_event[event_type_id]' value={event.event_type_id} />
              <div className='card event_type_stats mb-3'>
                <div className='card-body'>
                  <div className='event-types-stats row align-items-center'>
                    <div className='col col-adaptive-xs h4 mb-3 event-form__summary-title'>
                      <Calendar20 className='text-muted' />
                      {I18n.t('calendar.event_summary')}
                    </div>
                    <div className='col col-adaptive-sm mb-3'>
                      <dl className='stats-row bold text-future-success'>
                        {requestedDays > 0 && I18n.t('calendar.holiday_entitlements.requested_days_hint', { requested_days: requestedDays })}
                      </dl>
                      <dl className='stats-row text-muted italic'>
                        {workingDays > 0 && I18n.t('calendar.holiday_entitlements.working_days_hint', { working_days: workingDays })}
                      </dl>
                    </div>
                  </div>
                  <div className='holidays-stats'>
                    <VacationEventsStats stats={stats} stats_next_year={statsNextYear} />
                  </div>
                </div>
              </div>
            </>
            )
          : (
            <EventTypeSelect
              eventTypes={eventTypes}
              value={eventTypeId}
              onChange={setEventTypeId}
              disabled={isPersisted && isPending}
            />
            )}

        <div className='row event-form-row'>
          <div className='col col-adaptive-md flex-grow-0'>
            <EventFormCalendar
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateChange}
              disabledDates={calendarSettings.disabledDates}
              employeeId={employee.id}
              region={employee.region}
              halfDay={halfDay}
              onHalfDayChange={handleHalfDayChange}
            />

            {isMainVacation && (
              <div className='event-form__extra-days-warning--mobile'>
                <ExtraDaysWarning
                  show={needExtraDays}
                  allowInAdvance={allowInAdvance}
                  holidayInAdvanceEnabled={holidayInAdvanceEnabled}
                  onHolidayInAdvanceChange={handleHolidayInAdvanceChange}
                />
              </div>
            )}
          </div>

          <div className='col col-adaptive-sm'>
            {!isMainVacation && (
              <FileUpload
                attachment={event.attachment_relation}
                onFileChange={handleFileChange}
                onRemoveAttachment={handleRemoveAttachment}
                destroyAttachment={destroyAttachment}
                required={isAttachmentRequired}
                selectedFile={file}
              />
            )}

            <div className='form-group event-form-description'>
              <label>{I18n.t('activerecord.attributes.calendar/event.description')}</label>
              <textarea
                className='form-control autosize'
                style={{ minHeight: '120px' }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className='row'>
              <div className='col col-adaptive-md flex-grow-0'>
                <div className='geo-info left-text'>
                  {I18n.t('calendar.current_region')}: {employee.region}
                </div>
              </div>

              <div className='col col-adaptive-sm'>
                <div className='text-end'>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    disabled={isSubmitDisabled || isLoading || (isAttachmentRequired && !hasAttachment) || (overlapError && isMainVacation)}
                  >
                    {I18n.t('save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isMainVacation && (
          <div className='event-form__extra-days-warning--desktop'>
            <ExtraDaysWarning
              show={needExtraDays}
              allowInAdvance={allowInAdvance}
              holidayInAdvanceEnabled={holidayInAdvanceEnabled}
              onHolidayInAdvanceChange={handleHolidayInAdvanceChange}
            />
          </div>
        )}
      </form>
    </div>
  )
}
